import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { editarJson, enviarJson, enviarJsonSemConteudo, pedirJson } from "./api";

/* Pesquisas de satisfação (NPS e CES): a equipe configura e lê o resultado; o cliente responde
   pelo link do e-mail, sem login. O contrato é o de `api/app/dominios/pesquisas/`. */

export const CHAVE_DAS_PESQUISAS = ["pesquisas"] as const;

const uuid = z.string().uuid();

export const TIPOS_DE_PESQUISA = ["nps", "ces"] as const;
export type TipoDePesquisa = (typeof TIPOS_DE_PESQUISA)[number];

export const ROTULO_DO_TIPO_DE_PESQUISA: Record<TipoDePesquisa, string> = {
  nps: "NPS — recomendaria?",
  ces: "CES — foi fácil?",
};

export const ROTULO_DO_DISPARO: Record<string, string> = {
  resolvido: "Quando o chamado é resolvido",
  fechado: "Quando o cliente aceita a solução",
};

const esquemaPesquisa = z.object({
  id: uuid,
  nome: z.string(),
  tipo: z.enum(TIPOS_DE_PESQUISA),
  pergunta: z.string(),
  disparo_em: z.enum(["resolvido", "fechado"]),
  ativa: z.boolean(),
  enviados: z.number().int(),
  respondidos: z.number().int(),
  criado_em: z.string(),
});

const esquemaEnvio = z.object({
  id: uuid,
  pesquisa_id: uuid,
  pesquisa_nome: z.string(),
  tipo: z.enum(TIPOS_DE_PESQUISA),
  organizacao_id: uuid,
  organizacao_nome: z.string(),
  ticket_id: uuid,
  numero_do_chamado: z.number().int().nullable(),
  email: z.string(),
  estado: z.enum(["pendente", "respondida", "expirada"]),
  nota: z.number().int().nullable(),
  comentario: z.string().nullable(),
  criado_em: z.string(),
  enviado_em: z.string().nullable(),
  respondido_em: z.string().nullable(),
  expira_em: z.string(),
});

const esquemaResultado = z.object({
  pesquisa_id: uuid,
  nome: z.string(),
  tipo: z.enum(TIPOS_DE_PESQUISA),
  enviados: z.number().int(),
  respondidos: z.number().int(),
  taxa_de_resposta: z.number().nullable(),
  indicador: z.number().nullable(),
  promotores: z.number().int(),
  neutros: z.number().int(),
  detratores: z.number().int(),
  distribuicao: z.record(z.string(), z.number().int()),
  por_mes: z.array(
    z.object({
      mes: z.string(),
      enviados: z.number().int(),
      respondidos: z.number().int(),
      indicador: z.number().nullable(),
    }),
  ),
  comentarios: z.array(
    z.object({
      nota: z.number().int(),
      comentario: z.string(),
      organizacao_nome: z.string(),
      numero_do_chamado: z.number().int().nullable(),
      respondido_em: z.string(),
    }),
  ),
});

const esquemaResultados = z.object({
  desde: z.string().nullable(),
  ate: z.string().nullable(),
  pesquisas: z.array(esquemaResultado),
});

const esquemaPesquisaPublica = z.object({
  tipo: z.enum(TIPOS_DE_PESQUISA),
  pergunta: z.string(),
  escala_min: z.number().int(),
  escala_max: z.number().int(),
  rotulo_min: z.string(),
  rotulo_max: z.string(),
  situacao: z.enum(["aberta", "respondida", "expirada"]),
});

export type Pesquisa = z.infer<typeof esquemaPesquisa>;
export type EnvioDePesquisa = z.infer<typeof esquemaEnvio>;
export type ResultadoDaPesquisa = z.infer<typeof esquemaResultado>;
export type PesquisaPublica = z.infer<typeof esquemaPesquisaPublica>;

/* ---------------------------------------------------------------------------- helpers puros */

/** "40" para NPS (de -100 a 100), "6,0" para CES (média), "—" sem resposta. */
export function indicadorLegivel(tipo: TipoDePesquisa, valor: number | null): string {
  if (valor === null) return "—";
  return tipo === "nps"
    ? String(Math.round(valor))
    : new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 2 }).format(valor);
}

export function taxaLegivel(taxa: number | null): string {
  return taxa === null ? "—" : `${Math.round(taxa * 100)}%`;
}

/** A leitura do NPS em faixas convencionais (zona de excelência, qualidade, aperfeiçoamento, crítica). */
export function faixaDoNps(valor: number | null): string {
  if (valor === null) return "Sem respostas";
  if (valor >= 75) return "Excelência";
  if (valor >= 50) return "Qualidade";
  if (valor >= 0) return "Aperfeiçoamento";
  return "Crítica";
}

export function escalaDa(tipo: TipoDePesquisa): number[] {
  const [minimo, maximo] = tipo === "nps" ? [0, 10] : [1, 7];
  return Array.from({ length: maximo - minimo + 1 }, (_, i) => minimo + i);
}

/** Barras da distribuição: a largura de cada nota relativa à mais frequente. */
export function barrasDaDistribuicao(
  tipo: TipoDePesquisa,
  distribuicao: Record<string, number>,
): Array<{ nota: number; quantidade: number; proporcao: number }> {
  const maior = Math.max(1, ...Object.values(distribuicao));
  return escalaDa(tipo).map((nota) => {
    const quantidade = distribuicao[String(nota)] ?? 0;
    return { nota, quantidade, proporcao: quantidade / maior };
  });
}

export function validarPesquisa(form: { nome: string; pergunta: string }): string | null {
  if (!form.nome.trim()) return "Dê um nome à pesquisa.";
  if (form.pergunta.trim().length < 3) return "Escreva a pergunta que o cliente vai responder.";
  return null;
}

/* ---------------------------------------------------------------------------------- consultas */

export function usePesquisas(habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DAS_PESQUISAS, "lista"],
    enabled: habilitado,
    queryFn: ({ signal }) => pedirJson("/pesquisas", z.array(esquemaPesquisa), { signal }),
  });
}

export function useSalvarPesquisa() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id?: string; corpo: Record<string, unknown> }) =>
      id
        ? editarJson(`/pesquisas/${encodeURIComponent(id)}`, esquemaPesquisa, corpo)
        : enviarJson("/pesquisas", esquemaPesquisa, corpo),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DAS_PESQUISAS }),
  });
}

export interface FiltrosDeResultado {
  pesquisaId?: string;
  organizacaoId?: string;
  desde?: string;
  ate?: string;
}

export function useResultadosDasPesquisas(filtros: FiltrosDeResultado, habilitado = true) {
  const parametros = new URLSearchParams();
  if (filtros.pesquisaId) parametros.set("pesquisa_id", filtros.pesquisaId);
  if (filtros.organizacaoId) parametros.set("organizacao_id", filtros.organizacaoId);
  if (filtros.desde) parametros.set("desde", filtros.desde);
  if (filtros.ate) parametros.set("ate", filtros.ate);
  const consulta = parametros.toString();
  return useQuery({
    queryKey: [...CHAVE_DAS_PESQUISAS, "resultados", consulta],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      pedirJson(`/pesquisas/resultados${consulta ? `?${consulta}` : ""}`, esquemaResultados, {
        signal,
      }),
  });
}

export function useEnviosDePesquisa(estado: string, habilitado = true) {
  return useQuery({
    queryKey: [...CHAVE_DAS_PESQUISAS, "envios", estado],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      pedirJson(
        `/pesquisas/envios?limite=200${estado ? `&estado=${encodeURIComponent(estado)}` : ""}`,
        z.array(esquemaEnvio),
        { signal },
      ),
  });
}

export function useLinkDoEnvio() {
  return useMutation({
    mutationFn: (id: string) =>
      enviarJson(`/pesquisas/envios/${encodeURIComponent(id)}/link`, z.object({ link: z.string() }), {}),
  });
}

export function useReenviarConvite() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      enviarJson(`/pesquisas/envios/${encodeURIComponent(id)}/reenvio`, esquemaEnvio, {}),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: CHAVE_DAS_PESQUISAS }),
  });
}

/* ----------------------------------------------------------------------------------- pública */

export function usePesquisaPublica(token: string) {
  return useQuery({
    queryKey: [...CHAVE_DAS_PESQUISAS, "publica", token],
    retry: false,
    queryFn: ({ signal }) =>
      pedirJson(`/pesquisas/publicas/${encodeURIComponent(token)}`, esquemaPesquisaPublica, {
        signal,
      }),
  });
}

export function useResponderPesquisa(token: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ nota, comentario }: { nota: number; comentario: string }) =>
      enviarJsonSemConteudo(`/pesquisas/publicas/${encodeURIComponent(token)}/resposta`, {
        nota,
        comentario: comentario.trim() || null,
      }),
    onSuccess: () =>
      void cliente.invalidateQueries({ queryKey: [...CHAVE_DAS_PESQUISAS, "publica", token] }),
  });
}
